import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    LinearProgress,
    Alert,
    Chip,
    Divider,
    FormControlLabel,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    CloudUpload as UploadIcon,
    CheckCircle as SuccessIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Info as InfoIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNotify, useRefresh } from 'react-admin';

const ImportManager = () => {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [polling, setPolling] = useState(false);
    const [dryRun, setDryRun] = useState(false);
    const [errors, setErrors] = useState([]);
    
    const notify = useNotify();
    const refresh = useRefresh();

    // Listen for the custom event to open the dialog
    useEffect(() => {
        const handleOpenDialog = () => {
            console.log('Opening import dialog...');
            setOpen(true);
        };
        window.addEventListener('openImportDialog', handleOpenDialog);
        console.log('ImportManager: Event listener added for openImportDialog');
        return () => window.removeEventListener('openImportDialog', handleOpenDialog);
    }, []);

    // Polling for job status updates
    const pollJobStatus = useCallback(async (id) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/import/products/status/${id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobStatus(data);
                setErrors(data.errors || []);

                // Stop polling if job is completed, failed, or cancelled
                if (['completed', 'failed', 'cancelled'].includes(data.status)) {
                    setPolling(false);
                    if (data.status === 'completed') {
                        notify(`Import completed! Created: ${data.created_count}, Updated: ${data.updated_count}, Errors: ${data.error_count}`, { type: 'success' });
                        refresh(); // Refresh the imports list
                    } else if (data.status === 'failed') {
                        notify('Import failed. Check the errors for details.', { type: 'error' });
                    }
                }
            }
        } catch (error) {
            console.error('Error polling job status:', error);
            setPolling(false);
        }
    }, [notify, refresh]);

    // Start polling when jobId changes and status is processing
    useEffect(() => {
        if (jobId && polling) {
            const interval = setInterval(() => {
                pollJobStatus(jobId);
            }, 2000); // Poll every 2 seconds

            return () => clearInterval(interval);
        }
    }, [jobId, polling, pollJobStatus]);

    // Dropzone configuration
    const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
        if (rejectedFiles.length > 0) {
            notify('Please select a valid Excel file (.xlsx)', { type: 'error' });
            return;
        }

        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        }
    }, [notify]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
        },
        multiple: false,
        maxSize: 50 * 1024 * 1024, // 50MB limit
    });

    const handleUpload = async () => {
        if (!file) {
            notify('Please select a file first', { type: 'error' });
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('dry_run', dryRun);

            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/import/products`,
                {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setJobId(data.job_id);
                setJobStatus({ status: 'pending', ...data });
                setPolling(true);
                notify(dryRun ? 'Dry run started successfully' : 'Import started successfully', { type: 'success' });
            } else {
                const errorData = await response.json();
                notify(errorData.message || 'Upload failed', { type: 'error' });
            }
        } catch (error) {
            notify('Network error occurred', { type: 'error' });
            console.error('Upload error:', error);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setFile(null);
        setJobId(null);
        setJobStatus(null);
        setPolling(false);
        setErrors([]);
        setDryRun(false);
    };

    const downloadTemplate = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/import/products/template`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'products_import_template.xlsx';
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                document.body.removeChild(a);
                notify('Template downloaded successfully', { type: 'success' });
            }
        } catch (error) {
            notify('Error downloading template', { type: 'error' });
            console.error('Download error:', error);
        }
    };

    const getProgressPercentage = () => {
        if (!jobStatus || !jobStatus.total_rows) return 0;
        return Math.round((jobStatus.processed_rows / jobStatus.total_rows) * 100);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'completed': return 'success';
            case 'failed': return 'error';
            case 'processing': return 'info';
            case 'pending': return 'warning';
            default: return 'default';
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { minHeight: '500px' }
            }}
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Import Products</Typography>
                    <Button
                        startIcon={<DownloadIcon />}
                        onClick={downloadTemplate}
                        variant="outlined"
                        size="small"
                    >
                        Download Template
                    </Button>
                </Box>
            </DialogTitle>

            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            Upload an Excel (.xlsx) file with product data. The file must follow the exact template format with 12 columns in the specified order.
                            Images will be automatically processed with background removal and watermarking.
                        </Typography>
                    </Alert>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={dryRun}
                                onChange={(e) => setDryRun(e.target.checked)}
                                disabled={uploading || polling}
                            />
                        }
                        label="Dry Run (Preview only - no actual import)"
                        sx={{ mb: 2 }}
                    />
                </Box>

                {!jobStatus && (
                    <Box
                        {...getRootProps()}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDragActive ? 'primary.main' : 'grey.300',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            cursor: 'pointer',
                            backgroundColor: isDragActive ? 'action.hover' : 'transparent',
                            transition: 'all 0.2s ease',
                        }}
                    >
                        <input {...getInputProps()} />
                        <UploadIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            {isDragActive ? 'Drop the file here' : 'Drag & drop an Excel file here'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            or click to select a file (.xlsx only, max 50MB)
                        </Typography>
                        {file && (
                            <Box sx={{ mt: 2 }}>
                                <Chip
                                    label={`Selected: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`}
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                        )}
                    </Box>
                )}

                {jobStatus && (
                    <Box>
                        <Box sx={{ mb: 2 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography variant="h6">Import Progress</Typography>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Chip
                                        label={jobStatus.status}
                                        color={getStatusColor(jobStatus.status)}
                                        size="small"
                                    />
                                    {polling && (
                                        <Tooltip title="Refreshing...">
                                            <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} />
                                        </Tooltip>
                                    )}
                                </Box>
                            </Box>
                            
                            <LinearProgress
                                variant="determinate"
                                value={getProgressPercentage()}
                                sx={{ mb: 1 }}
                            />
                            
                            <Typography variant="body2" color="text.secondary">
                                {jobStatus.processed_rows || 0} / {jobStatus.total_rows || 0} rows processed ({getProgressPercentage()}%)
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            <Chip
                                icon={<SuccessIcon />}
                                label={`Created: ${jobStatus.created_count || 0}`}
                                color="success"
                                variant="outlined"
                            />
                            <Chip
                                icon={<InfoIcon />}
                                label={`Updated: ${jobStatus.updated_count || 0}`}
                                color="info"
                                variant="outlined"
                            />
                            <Chip
                                icon={<ErrorIcon />}
                                label={`Errors: ${jobStatus.error_count || 0}`}
                                color="error"
                                variant="outlined"
                            />
                        </Box>

                        {errors.length > 0 && (
                            <Box>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h6" gutterBottom>
                                    Errors ({errors.length})
                                </Typography>
                                <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                                    {errors.slice(0, 10).map((error, index) => (
                                        <ListItem key={index} divider>
                                            <ListItemIcon>
                                                <ErrorIcon color="error" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={`Row ${error.row_number} - ${error.field}`}
                                                secondary={error.error_message}
                                            />
                                        </ListItem>
                                    ))}
                                    {errors.length > 10 && (
                                        <ListItem>
                                            <ListItemText
                                                secondary={`... and ${errors.length - 10} more errors`}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose}>
                    {jobStatus?.status === 'completed' || jobStatus?.status === 'failed' ? 'Close' : 'Cancel'}
                </Button>
                {!jobStatus && (
                    <Button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        variant="contained"
                        startIcon={uploading ? <RefreshIcon sx={{ animation: 'spin 1s linear infinite' }} /> : <UploadIcon />}
                    >
                        {uploading ? 'Uploading...' : dryRun ? 'Start Dry Run' : 'Start Import'}
                    </Button>
                )}
            </DialogActions>

            <style jsx>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </Dialog>
    );
};

export default ImportManager;
